import { PRIVACY_PATH, TERMS_PATH } from '../features/legal/legalRoutes';
import type { LegalAcceptanceRequired, LegalDocumentRef } from '../types/legal';

/**
 * Whether a server-sent document URL is safe to put in an `href`: an absolute http(s) URL, or a
 * same-origin path. Anything else (a `javascript:` URL, a protocol-relative `//host`) is refused.
 */
const isSafeDocumentUrl = (url: string): boolean => {
  if (url.startsWith('/')) return !url.startsWith('//');
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

const parseDocument = (value: unknown, fallbackUrl: string): LegalDocumentRef | null => {
  if (typeof value !== 'object' || value === null) return null;
  const { version, url } = value as Record<string, unknown>;

  // The version is sent back verbatim on accept, so it must be exactly what the server sent.
  if (typeof version !== 'string' || !version.trim()) return null;

  // A missing or unusable link still leaves something to accept: point at our own copy of the page
  // rather than dropping the document, which would hide it while the API keeps refusing requests.
  const safeUrl = typeof url === 'string' && isSafeDocumentUrl(url.trim()) ? url.trim() : fallbackUrl;

  return { version, url: safeUrl };
};

/**
 * Reads `legalAcceptanceRequired` off a server response (`/api/auth/me`, a 403
 * `legal_acceptance_required`, a 409 `version_changed`) through guards rather than a cast.
 *
 * Web and the API deploy separately, so the field can be absent (an older API) or malformed. Both
 * fail open: null, meaning "nothing to accept", so a rollout can never lock every coach out. Only
 * an object with at least one well-formed document blocks the app.
 */
export const parseLegalAcceptanceRequired = (value: unknown): LegalAcceptanceRequired | null => {
  if (typeof value !== 'object' || value === null) return null;
  const { terms, privacy } = value as Record<string, unknown>;

  const parsed: LegalAcceptanceRequired = {
    terms: parseDocument(terms, TERMS_PATH),
    privacy: parseDocument(privacy, PRIVACY_PATH),
  };

  return parsed.terms || parsed.privacy ? parsed : null;
};
